import * as React from 'react';

import { cn } from '../base/utils';

export type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning';
export type BadgeShape = 'pill' | 'rounded';

export const badgeVariants = ({
  variant = 'default',
  shape = 'pill',
  className,
}: { variant?: BadgeVariant | null; shape?: BadgeShape; className?: string } = {}) => cn(
  'inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  shape === 'pill' ? 'rounded-full' : 'rounded-md',
  variant === 'secondary' && 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
  variant === 'destructive' && 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
  variant === 'outline' && 'text-foreground',
  variant === 'success' && 'border-transparent bg-success/15 text-success',
  variant === 'warning' && 'border-transparent bg-warning/15 text-warning-foreground',
  (!variant || variant === 'default') && 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
  className,
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: BadgeVariant;
  shape?: BadgeShape;
}

export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(({ className, variant, shape, ...props }, ref) => (
  <div ref={ref} className={badgeVariants({ variant, shape, className })} {...props} />
));
Badge.displayName = 'Badge';

export const RoundedBadge = React.forwardRef<HTMLDivElement, Omit<BadgeProps, 'shape'>>((props, ref) => (
  <Badge ref={ref} shape="rounded" {...props} />
));
RoundedBadge.displayName = 'RoundedBadge';
